import { Button, Col, Modal, Row, Space, Table } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RouteChildrenProps } from "react-router";
import { Link } from "react-router-dom";
import { REPORTQUERYPARAMS, ReportStatesEnum } from "../../../../constants/constants";
import { LOCAL_STORAGE_KEY } from "../../../../constants/local";
import { ROUTE_PATHS } from "../../../../constants/url-config";
import { ScenarioFrontierModel } from "../../../../models/scenario-frontier.model";
import { ScenarioFrontierService } from "../../../../services/scenario-frontier.service";
import { DATE_UTIL } from "../../../../utils/dateUtils";
import HISTORY from "../../../../utils/history.helper";
import i18n from "../../../../utils/i18n";
import UTIL from "../../../../utils/utils";
import { AddIcon, SettingIcon } from "../../../atoms/icon";
import { quarters } from "../select-period";
import "./index.scss";

interface Props extends RouteChildrenProps { }

const ScenarioFrontierReports = (props: Props) => {

    const { t } = useTranslation();
    const [data, setData] = useState<ScenarioFrontierModel[]>([]);
    const [dataLoading, setDataLoading] = useState(false);

    const columns = [
        {
            title: i18n.t("ScenarioFrontier.lbReportName"),
            dataIndex: 'report_name',
            key: 'report_name',
            render: (text: string, record: ScenarioFrontierModel) =>
                <span className="blue-text" >
                    {`Scenario Report - ${record.fiscalYear} - ${quarters.find(x => x.value === record.quarter)?.text}`}
                </span >
        },
        {
            title: i18n.t("ScenarioFrontier.lbReportOwner"),
            dataIndex: 'created_by',
            key: 'created_by',
        },
        {
            title: i18n.t("ScenarioFrontier.lbStatus"),
            dataIndex: 'submit_state',
            key: 'submit_state',
            render: (text: string) => {
                const dom = UTIL.getLossStatesDOM(text);
                return (<span className="status-text">
                    <span style={{ backgroundColor: dom.color }} className="dot"></span>{dom.text}
                </span>)
            }
        },
        {
            title: i18n.t("ScenarioFrontier.lbDateCreated"),
            dataIndex: 'created_on',
            key: 'created_on',
            render: (date: string) => DATE_UTIL.convertServerToViewFormat(date)
        },
        {
            title: i18n.t("ScenarioFrontier.lbActions"),
            key: 'action',
            render: (record: ScenarioFrontierModel) => renderActions(record)

        }
    ];

    const submitReport = (item: ScenarioFrontierModel) => {

        const submit = async () => {
            if (item.id) {
                await ScenarioFrontierService.submitForApproval(item.id);
                getData();
            }
        }

        Modal.confirm({
            content: i18n.t("ScenarioFrontier.confirmSubmit"),
            icon: '',
            onOk: () => submit(),
            width: 304,
            okText: i18n.t("ScenarioFrontier.btnSubmit"),
            cancelText: i18n.t("Global.lbCancel")
        });
    }

    const renderActions = (record: ScenarioFrontierModel) => {

        switch (record.submit_state) {
            case ReportStatesEnum.Approved:
            case ReportStatesEnum.Submitted:
                return (<Link className="blue-text"
                    to={`${ROUTE_PATHS.ScenarioFrontierReportDetails}/${record.id}`}
                > {t("ScenarioFrontier.lbView")} </Link >);
            case ReportStatesEnum.Draft:
                return (<span className="blue-text action-text" onClick={() => generateReport(record)}> {t("ScenarioFrontier.lbEdit")}</span>);
            case ReportStatesEnum.Submitting:
                return (
                    <Space>
                        <span className="blue-text action-text" onClick={() => generateReport(record)}> {t("ScenarioFrontier.lbEdit")}</span>

                        <span className="blue-text action-text" onClick={() => submitReport(record)}> {t("ScenarioFrontier.btnSubmit")}</span>
                    </Space>
                );
            case ReportStatesEnum.Rejected:
                return (
                    <Space>
                        <span className="blue-text action-text" onClick={() => generateReport(record)}> {t("ScenarioFrontier.lbEdit")}</span>
                        <Link className="blue-text" to={`${ROUTE_PATHS.ScenarioFrontierReportDetails}/${record.id}`}> {t("ScenarioFrontier.lbView")} </Link >
                    </Space>
                );

            default:
                break;
        }

    }

    const generateReport = (record?: ScenarioFrontierModel) => {
        let year = 0;
        let quarter = 0;
        if (record) {
            year = record.fiscalYear;
            quarter = record.quarter;
        } else {
            try {
                year = Number(localStorage.getItem(LOCAL_STORAGE_KEY.SCENARIO_FRONTIER_YEAR));
                quarter = Number(localStorage.getItem(LOCAL_STORAGE_KEY.SCENARIO_FRONTIER_QUARTER));
            } catch (error) {
                console.error("Can not convert Year and Quarter from LocalStorege");
            }
        }

        if (!year || !quarter) {
            HISTORY.push({
                pathname: ROUTE_PATHS.ScenarioFrontier,
                state: { nextPathname: ROUTE_PATHS.ScenarioFrontierNewReport }
            });
        } else {
            HISTORY.push({
                pathname: ROUTE_PATHS.ScenarioFrontierNewReport,
                search: `?${REPORTQUERYPARAMS.year}=${year}&${REPORTQUERYPARAMS.quarter}=${quarter}`
            });
        }
    }

    const configuration = () => {
        HISTORY.push(ROUTE_PATHS.ScenarioFrontierConfiguration);
    }

    const getData = () => {
        setDataLoading(true);
        ScenarioFrontierService.getReports().then(res => {
            setData(res.data);
        }).finally(() => {
            setDataLoading(false)
        });
    }

    useEffect(() => {
        getData();
    }, [])

    return (
        <div className='scenario-frontier-Reports-wrapper'>
            <Row className="header-page">
                <Col sm={12}>
                    <h4>
                        {t("ScenarioFrontier.lbAllReports")}
                    </h4>
                </Col>
                <Col sm={12} className='action-box'>
                    <Space>
                        {/* <Button shape="round" type="default" className="setting-btn" onClick={configuration} >
                            <SettingIcon className="svg-icon" /> {t("ScenarioFrontier.btnConfiguration")}
                        </Button> */}
                        <Button type="primary" shape="round" onClick={() => generateReport()}>
                            <AddIcon className="btn-icon" /> {t("ScenarioFrontier.btnNewReport")}
                        </Button>
                    </Space>
                </Col>
            </Row>
            <Row>
                <Col span={24}>
                    <Table
                        loading={dataLoading}
                        dataSource={data}
                        columns={columns} rowKey="id" />
                </Col>
            </Row>
        </div>
    )
}

export default ScenarioFrontierReports;